import React from 'react';
import { 
  HomeIcon, 
  UsersIcon, 
  UserPlusIcon, 
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PlusCircleIcon  // Import the icon for Add Workspace
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import aizops from "../../assets/images/Company-Logo.png";

const Sidebar = ({ activeTab, setActiveTab, userRole, sidebarOpen, setSidebarOpen, isMobile, user }) => {
  
  // Make sure userRole is always in lowercase for consistent comparison
  const normalizedRole = userRole?.toLowerCase() || 'employee';
  
  // Define all menu items with role-based access
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: HomeIcon, roles: ['employee', 'manager', 'hr', 'admin', 'superadmin'] },
    { id: 'employeemanagement', label: 'Employee Management', icon: BuildingOfficeIcon, roles: ['hr', 'admin', 'superadmin','employee',] },
    { id: 'add-employee', label: 'Add Employee', icon: UserPlusIcon, roles: ['hr', 'admin', 'superadmin', 'employee'] },
    { id: 'attendance', label: 'Attendance', icon: ClockIcon, roles: ['employee', 'manager', 'hr', 'admin', 'superadmin'] },
    { id: 'leaves', label: 'Leave Requests', icon: CalendarIcon, roles: ['employee', 'manager', 'hr', 'admin', 'superadmin'] },
    { id: 'payroll', label: 'Payroll', icon: CurrencyDollarIcon, roles: ['employee', 'manager', 'hr', 'admin', 'superadmin'] },
    { id: 'performance', label: 'Performance', icon: ChartBarIcon, roles: ['employee', 'manager', 'hr', 'admin', 'superadmin'] },
    { id: 'reports', label: 'Reports', icon: DocumentTextIcon, roles: ['hr', 'admin', 'superadmin', 'employee'] }, // Changed icon
    { id: 'offerletter', label: 'Offer Letter', icon: DocumentTextIcon, roles: ['superadmin', 'hr', 'admin', 'employee'] },
    { id: 'add-workspace', label: 'Add Workspace', icon: PlusCircleIcon, roles: ['superadmin', 'admin', 'hr', 'employee'] }, // New menu item
    { id: 'settings', label: 'Settings', icon: Cog6ToothIcon, roles: ['superadmin','hr','admin','employee'] },
    { id: 'add-expense', label: 'Add Expense', icon: CurrencyDollarIcon, roles: ['hr', 'admin', 'superadmin', 'employee'] },
    { id: 'recruitment', label: 'Recruitment', icon: UserGroupIcon, roles: ['hr', 'admin', 'superadmin', 'employee'] },
    { id: 'policydocument', label: 'Privacy Policy', icon: UserGroupIcon, roles: ['hr', 'admin', 'superadmin', 'employee'] },
  ];

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const scrollToTopWithDelay = () => {
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        window.scrollTo(0, 0);
      }
    }, 100);
  };

  const getRoleBadgeColor = () => {
    switch(normalizedRole) {
      case 'superadmin': return 'bg-red-100 text-red-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'hr': return 'bg-blue-100 text-blue-700';
      case 'manager': return 'bg-green-100 text-green-700';
      case 'employee': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleDisplayName = () => {
    switch(normalizedRole) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'hr': return 'HR Manager';
      case 'manager': return 'Manager';
      case 'employee': return 'Employee';
      default: return normalizedRole?.toUpperCase() || 'User';
    }
  };

  // Get user name from props or localStorage
  const getUserName = () => {
    if (user?.name) return user.name;
    if (user?.fullName) return user.fullName;
    
    // Try to get from localStorage as fallback
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        return parsedUser.name || parsedUser.fullName || parsedUser.username || 'User';
      } catch (e) {
        return 'User';
      }
    }
    return 'User';
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const name = getUserName();
    if (name === 'User') return normalizedRole?.charAt(0).toUpperCase() || 'U';
    
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const userName = getUserName();
  const userInitials = getUserInitials();
  const roleDisplayName = getRoleDisplayName();

  // Debug logging to check what role is being received
  console.log('Sidebar received userRole:', userRole);
  console.log('Normalized role:', normalizedRole);
  console.log('Available menu items for this role:', menuItems.filter(item => 
    item.roles.includes(normalizedRole)
  ).map(item => item.label));

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Mobile Toggle Button */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-md border border-gray-200"
        >
          <Bars3Icon className="w-5 h-5 text-gray-600" />
        </button>
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed md:relative z-30 bg-white border-r border-gray-200
        transition-all duration-300 flex flex-col h-full shadow-sm
        ${sidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full md:translate-x-0 md:w-20'}
        ${isMobile ? 'top-0 left-0' : ''}
      `}>
        {/* Logo & Toggle Button */}
        <div className={`p-4 md:p-6 border-b border-gray-200 ${!sidebarOpen && !isMobile ? 'justify-center' : ''}`}>
          <div className={`flex items-center ${!sidebarOpen && !isMobile ? 'justify-center' : 'justify-between'}`}>
            <div className={`flex items-center ${!sidebarOpen && !isMobile ? 'justify-center' : 'gap-3'}`}>
              
             
              {(!isMobile && sidebarOpen) && (
                <span className="font-bold text-lg text-gray-800 whitespace-nowrap"><Link 
              to="/" 
              className="flex items-center gap-2" 
              onClick={scrollToTopWithDelay}
            >
              <div>
                <img 
                  src={aizops} 
                  alt="Aizops logo" 
                  style={{
                    width: '120px',
                    height: 'auto',
                    maxHeight: '40px',
                    objectFit: 'contain'
                  }}
                />
                <div className="text-sm text-[9px] text-black text-gray-200 mt-2 " >
                  Intelligent Operations
                </div>
              </div>
            </Link></span>
              )}
            </div>
            
            {/* Desktop Toggle Button */}
            {!isMobile && (
              <button
                onClick={handleToggleSidebar}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {sidebarOpen ? (
                  <ChevronLeftIcon className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* User Info */}
        <div className={`p-4 border-b border-gray-200 ${!sidebarOpen && !isMobile ? 'text-center' : ''}`}>
          <div className={`flex ${!sidebarOpen && !isMobile ? 'flex-col items-center' : 'items-center gap-3'}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center font-bold text-white flex-shrink-0">
              {userInitials}
            </div>
            {(!isMobile && sidebarOpen) && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{userName}</p>
                <div className="mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full inline-block ${getRoleBadgeColor()}`}>
                    {roleDisplayName}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Show role badge when sidebar is collapsed */}
          {(isMobile || (!sidebarOpen && !isMobile)) && (
            <div className="mt-2">
              <span className={`text-xs px-2 py-0.5 rounded-full inline-block ${getRoleBadgeColor()}`}>
                {roleDisplayName}
              </span>
            </div>
          )}
        </div>
        
        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => {
              // Check if current role has access to this menu item
              const hasAccess = item.roles.includes(normalizedRole);
              
              if (!hasAccess) return null;
              
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      setActiveTab(item.id);
                      if (isMobile) setSidebarOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group
                      ${isActive 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                      ${!sidebarOpen && !isMobile ? 'justify-center' : ''}
                    `}
                    title={!sidebarOpen && !isMobile ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {(!isMobile && sidebarOpen) && (
                      <span className="text-sm whitespace-nowrap">{item.label}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        
        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-900 
              hover:bg-red-50 hover:text-red-600 transition-all group
              ${!sidebarOpen && !isMobile ? 'justify-center' : ''}
            `}
            title={!sidebarOpen && !isMobile ? 'Logout' : ''}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
            {(!isMobile && sidebarOpen) && <span className="text-sm whitespace-nowrap">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;